import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $accountByIdStore } from '@lib/store';
import { useAccountFlow } from '@shared/hooks';
import type { AccountByIdResponse } from '@shared/api';
import { BaseInput, Button, Modal } from '@shared/widgets';

interface FormData {
  label: string;
  costCentre: string;
  category: string;
}

interface EditDetailsModalProps {
  show: boolean;
  onClose: () => void;
  account: AccountByIdResponse;
  initial: Partial<FormData>;
}

export const EditDetailsModal = (props: EditDetailsModalProps) => {
  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    defaultValues: { label: props.initial.label || '', costCentre: props.initial.costCentre || '', category: props.initial.category || '' },
  });
  const { t } = useTranslation('account');

  const closeHandler = () => {
    props.onClose();
    reset({ label: props.initial.label || '', costCentre: props.initial.costCentre || '', category: props.initial.category || '' });
  };
  const submitHandler = async (data: FormData) => {
    await $accountByIdStore.events.updateDetails({
      accountId: props.account.id,
      label: data.label,
      costCentre: data.costCentre,
      category: data.category,
    });
    props.onClose();
  };

  const accountFlow = useAccountFlow(props.account.id);
  if (!accountFlow?.editAccountLabel && !accountFlow?.editCostCentre && !accountFlow?.editAccountCategory) return null;
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('sidebar.modals.editDetails.title')}>
      <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
        {accountFlow?.editAccountLabel && (
          <Controller
            name='label'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('sidebar.modals.editDetails.placeholders.accountLabel')}
                placeholder={t('sidebar.modals.editDetails.placeholders.accountLabel')}
                required
                error={error?.message}
              />
            )}
          />
        )}
        {accountFlow?.editCostCentre && (
          <Controller
            name='costCentre'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('sidebar.modals.editDetails.placeholders.costCentre')}
                placeholder={t('sidebar.modals.editDetails.placeholders.costCentre')}
                required
                error={error?.message}
              />
            )}
          />
        )}
        {accountFlow?.editAccountCategory && (
          <Controller
            name='category'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('sidebar.modals.editDetails.placeholders.accountCategory')}
                placeholder={t('sidebar.modals.editDetails.placeholders.accountCategory')}
                required
                error={error?.message}
              />
            )}
          />
        )}
        <Button className='mt-5' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('buttons.saveChanges', { ns: 'common' })}
        </Button>
      </form>
    </Modal>
  );
};
